import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import styled, { createGlobalStyle } from 'styled-components';
import { QRCodeSVG } from 'qrcode.react';
import {
  PUBLIC_SITE_URL,
  adminDeleteMouse,
  adminGenerateMice,
  adminGetJourney,
  adminListMice,
  adminRenameTown,
  adminResetMouse,
  adminSetJourney,
  adminUpdateMouse,
  mouseUrl,
} from '../../../services/miceTrailService';
import belvoirLogo from '../../../assets/logo-transparent.png';

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  align-items: flex-end;
  margin-bottom: 1.25rem;
`;

const Label = styled.label`
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: ${(p) => p.theme.colors.secondary};
`;

const Input = styled.input`
  padding: 0.6rem 0.85rem;
  border: 2px solid rgba(16, 29, 64, 0.12);
  border-radius: 8px;
  font: inherit;
  font-size: 0.92rem;
  text-transform: none;
  letter-spacing: 0;
  font-weight: 400;
  &:focus { outline: none; border-color: ${(p) => p.theme.colors.primary}; }
`;

const Select = styled.select`
  padding: 0.6rem 0.85rem;
  border: 2px solid rgba(16, 29, 64, 0.12);
  border-radius: 8px;
  font: inherit;
  font-size: 0.92rem;
  background: #fff;
`;

const Btn = styled.button`
  padding: 0.62rem 1.1rem;
  border-radius: 8px;
  border: 2px solid ${(p) => (p.$danger ? '#b3261e' : p.theme.colors.primary)};
  background: ${(p) => (p.$solid ? p.theme.colors.primary : '#fff')};
  color: ${(p) => (p.$solid ? '#fff' : p.$danger ? '#b3261e' : p.theme.colors.primary)};
  font-weight: 600;
  font-size: 0.88rem;
  cursor: pointer;
  &:hover:not(:disabled) { opacity: 0.85; }
  &:disabled { opacity: 0.45; cursor: not-allowed; }
`;

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-bottom: 1.25rem;
`;

const Chip = styled.div`
  min-width: 120px;
  padding: 0.75rem 1rem;
  border-radius: 10px;
  background: ${(p) => p.theme.colors.offWhite};
  border: 1px solid rgba(16, 29, 64, 0.08);
  strong { display: block; font-size: 1.5rem; color: ${(p) => p.theme.colors.primary}; }
  span { font-size: 0.75rem; color: ${(p) => p.theme.colors.secondary}; text-transform: uppercase; letter-spacing: 0.05em; }
`;

const Banner = styled.div`
  margin-bottom: 1rem;
  padding: 0.85rem 1rem;
  border-radius: 10px;
  background: ${(p) => (p.$error ? '#f8d7da' : '#d1ecf1')};
  border: 1px solid ${(p) => (p.$error ? '#f5c6cb' : '#bee5eb')};
  color: ${(p) => (p.$error ? '#721c24' : '#0c5460')};
  font-size: 0.9rem;
  line-height: 1.5;
  code { background: rgba(0, 0, 0, 0.07); padding: 0.1rem 0.35rem; border-radius: 4px; }
`;

const Town = styled.section`
  margin-bottom: 1.5rem;
  border: 1px solid rgba(16, 29, 64, 0.1);
  border-radius: 12px;
  overflow: hidden;
`;

const TownHead = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem;
  padding: 0.85rem 1rem;
  background: ${(p) => p.theme.colors.offWhite};
  h4 { margin: 0; font-size: 1.15rem; color: ${(p) => p.theme.colors.primary}; }
  .count { color: ${(p) => p.theme.colors.secondary}; font-size: 0.88rem; }
  .spacer { flex: 1; }
`;

const TableWrap = styled.div`overflow-x: auto;`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.88rem;
  th, td { padding: 0.6rem 0.85rem; text-align: left; vertical-align: top; border-top: 1px solid rgba(16, 29, 64, 0.07); }
  th { font-size: 0.7rem; letter-spacing: 0.06em; text-transform: uppercase; color: ${(p) => p.theme.colors.secondary}; white-space: nowrap; }
  td.actions { white-space: nowrap; }
  td small { display: block; color: #6b7280; }
  .code { font-family: ui-monospace, Menlo, monospace; font-size: 0.82rem; }
`;

const Badge = styled.span`
  display: inline-block;
  padding: 0.15rem 0.6rem;
  border-radius: 999px;
  font-size: 0.74rem;
  font-weight: 700;
  background: ${(p) => (p.$found ? '#d4edda' : '#fff3cd')};
  color: ${(p) => (p.$found ? '#155724' : '#856404')};
`;

const SpotInput = styled.input`
  width: 100%;
  min-width: 170px;
  padding: 0.4rem 0.55rem;
  border: 1px solid rgba(16, 29, 64, 0.15);
  border-radius: 6px;
  font: inherit;
  &:focus { outline: none; border-color: ${(p) => p.theme.colors.primary}; }
`;

const JourneyBox = styled.section`
  margin-bottom: 1.5rem;
  padding: 1rem 1.1rem 1.15rem;
  border: 1px solid rgba(16, 29, 64, 0.12);
  border-radius: 12px;
  background: ${(p) => p.theme.colors.offWhite};
  h4 { margin: 0 0 0.35rem; font-size: 1.05rem; color: ${(p) => p.theme.colors.primary}; }
  p { margin: 0 0 0.85rem; font-size: 0.88rem; color: #4b5563; line-height: 1.5; }
  .phase { font-weight: 700; color: ${(p) => p.theme.colors.primary}; }
  .hints { display: grid; gap: 0.75rem; margin-top: 0.85rem; }
  .hint-row { display: grid; grid-template-columns: 1fr 1.4fr; gap: 0.55rem; }
  .photos { display: grid; grid-template-columns: repeat(auto-fill, minmax(160px, 1fr)); gap: 0.85rem; }
  .photos a { display: block; color: inherit; text-decoration: none; }
  .photos img { display: block; width: 100%; aspect-ratio: 1; object-fit: cover; border-radius: 8px; background: #fff; }
  .photos span, .photos small { display: block; margin-top: 0.35rem; }
  .photos span { font-weight: 700; font-size: 0.86rem; color: ${(p) => p.theme.colors.primary}; }
  .photos small { color: #6b7280; font-size: 0.75rem; }
  @media (max-width: 720px) { .hint-row { grid-template-columns: 1fr; } }
`;

const LinkBtn = styled.button`
  border: 0;
  background: none;
  padding: 0.2rem 0.4rem;
  color: ${(p) => (p.$danger ? '#b3261e' : p.theme.colors.primary)};
  font-weight: 600;
  font-size: 0.82rem;
  cursor: pointer;
  text-decoration: underline;
`;

// ---------------------------------------------------------------------------
// Printing
// ---------------------------------------------------------------------------

const PrintGlobal = createGlobalStyle`
  @page { size: A4; margin: 10mm; }
  @media print {
    html, body { height: auto !important; overflow: visible !important; background: #fff !important; }
    body > *:not(#mice-print-root) { display: none !important; }
    #mice-print-root { position: static !important; inset: auto !important; overflow: visible !important; background: #fff !important; }
    #mice-print-root .no-print { display: none !important; }
    #mice-print-root .sheet { box-shadow: none !important; margin: 0 !important; }
  }
`;

const PrintRoot = styled.div`
  position: fixed;
  inset: 0;
  z-index: 100000;
  overflow: auto;
  background: #dfe3ea;
  color: #101d40;
  font-family: 'Inter', -apple-system, sans-serif;
`;

const PrintBar = styled.div`
  position: sticky;
  top: 0;
  z-index: 2;
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  align-items: flex-end;
  padding: 0.85rem 1.25rem;
  background: #fff;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.15);
  .spacer { flex: 1; }
  .hint { font-size: 0.82rem; color: #4b5563; max-width: 36rem; }
`;

const Sheet = styled.div`
  width: 190mm;
  height: 277mm;
  margin: 1rem auto;
  background: #fff;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.18);
  box-sizing: border-box;
  display: grid;
  grid-template-columns: repeat(${(p) => p.$cols}, ${(p) => (p.$cellW ? `${p.$cellW}mm` : '1fr')});
  grid-auto-rows: ${(p) => (p.$cellH ? `${p.$cellH}mm` : '1fr')};
  align-content: start;
  justify-content: center;
  page-break-after: always;
  break-after: page;
  overflow: hidden;
  &:last-child { page-break-after: auto; break-after: auto; }
`;

const SheetLabel = styled.p`
  width: 190mm;
  margin: 1.25rem auto -0.5rem;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #4b5563;
`;

const Tag = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${(p) => (p.$compact ? '0.9mm' : `${p.$gap || 1.8}mm`)};
  position: relative;
  padding: ${(p) => {
    if (p.$compact) return '1.4mm 1.1mm 1.6mm';
    return p.$punch ? '10.5mm 3mm 3mm' : '3mm';
  }};
  border: 0.3mm dashed #9aa3b2;
  text-align: center;
  break-inside: avoid;

  .hole {
    width: 2.6mm;
    height: 2.6mm;
    border: 0.25mm dashed #9aa3b2;
    border-radius: 50%;
    flex-shrink: 0;
  }
  .punch {
    position: absolute;
    top: 3mm;
    left: 50%;
    width: 6mm;
    height: 6mm;
    transform: translateX(-50%);
    box-sizing: border-box;
    border: 0.35mm solid #9aa3b2;
    border-radius: 50%;
  }
  .punch::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 1mm;
    height: 1mm;
    transform: translate(-50%, -50%);
    border-radius: 50%;
    background: #9aa3b2;
  }

  .brand { display: flex; align-items: center; gap: 2mm; font-size: ${(p) => p.$brand}pt; font-weight: 800; letter-spacing: ${(p) => (p.$compact ? '0.08em' : '0.18em')}; text-transform: uppercase; color: #101d40; line-height: 1.1; }
  .brand img { display: block; height: ${(p) => p.$logo}mm; width: auto; }
  .brand span { padding-left: 2mm; border-left: 0.3mm solid #9aa3b2; }
  .cta { font-size: ${(p) => p.$cta}pt; font-weight: 800; color: #c8102e; line-height: 1.1; }
  .cta svg { display: inline-block; height: 0.95em; width: 1.52em; vertical-align: -0.12em; margin-left: 0.3em; }
  .sub { font-size: ${(p) => p.$sub}pt; font-weight: 700; color: #101d40; line-height: 1.2; max-width: 95%; text-wrap: balance; }
  .qr { width: ${(p) => p.$qr}mm; height: ${(p) => p.$qr}mm; flex-shrink: 0; }
  .qr svg { display: block; width: 100%; height: 100%; }
  .meta { font-size: ${(p) => p.$meta}pt; font-weight: 700; color: #101d40; line-height: 1.15; }
  .url { font-size: ${(p) => p.$url}pt; color: #4b5563; word-break: break-all; }
`;

const LAYOUTS = {
  label: {
    label: 'Mouse tags (49 per page)',
    cols: 7,
    perPage: 49,
    qr: 18,
    brand: 5,
    cta: 0,
    meta: 6,
    url: 0,
    compact: true,
    hole: true,
    cellW: 27,
    cellH: 39.5,
    ecc: 'Q',
    brandText: 'Belvoir',
  },
  tiny: {
    label: 'Tiny labels (80 per page)',
    cols: 8,
    perPage: 80,
    qr: 15,
    brand: 0,
    cta: 0,
    meta: 5,
    url: 0,
    compact: true,
    cellW: 23.5,
    cellH: 27.5,
    ecc: 'Q',
  },
  small: { label: 'Small tags (12 per page)', cols: 3, perPage: 12, qr: 28, punch: true, gap: 1.4, logo: 4.5, brand: 6.5, cta: 10, sub: 7, meta: 8, url: 6 },
  medium: { label: 'Medium cards (6 per page)', cols: 2, perPage: 6, qr: 42, punch: true, logo: 5.5, brand: 8.5, cta: 15, sub: 10, meta: 10, url: 7.5 },
  large: { label: 'Large signs (2 per page)', cols: 1, perPage: 2, qr: 80, punch: true, logo: 9, brand: 11, cta: 22, sub: 15, meta: 14, url: 10 },
};

const Checklist = styled.div`
  width: 190mm;
  min-height: 277mm;
  margin: 1rem auto;
  padding: 8mm;
  box-sizing: border-box;
  background: #fff;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.18);
  h2 { margin: 0 0 1mm; font-size: 16pt; }
  p { margin: 0 0 5mm; font-size: 9pt; color: #4b5563; }
  h3 { margin: 6mm 0 2mm; font-size: 12pt; break-after: avoid; }
  table { width: 100%; border-collapse: collapse; font-size: 9.5pt; }
  th, td { padding: 1.6mm 2mm; border: 0.3mm solid #9aa3b2; text-align: left; }
  tr { break-inside: avoid; }
  .tick { width: 9mm; }
  .box { display: inline-block; width: 4.5mm; height: 4.5mm; border: 0.4mm solid #101d40; }
`;

const chunk = (items, size) => {
  const out = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
};

const MouseSymbol = () => (
  <svg viewBox="0 0 32 20" aria-hidden="true">
    <path d="M7.5 14.5C4 15.5 1.2 14 1.6 10.6C1.9 8.3 3.8 7.4 5.4 8.2" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <path d="M6.5 14.2C6.5 9 10.5 5.6 15.5 5.6C19.6 5.6 22.6 7.4 25.2 9.6L29.6 12.2C30.5 12.7 30.3 13.9 29.3 14.1L25.4 14.9C22.4 16 18.6 16.6 14 16.6H8.6C7.4 16.6 6.5 15.6 6.5 14.2Z" fill="currentColor" />
    <circle cx="19.6" cy="5.4" r="3.6" fill="currentColor" stroke="#fff" strokeWidth="0.9" />
    <circle cx="24.6" cy="10.6" r="0.9" fill="#fff" />
    <ellipse cx="11" cy="17.3" rx="2" ry="1" fill="currentColor" />
    <ellipse cx="19" cy="17.1" rx="2" ry="1" fill="currentColor" />
  </svg>
);

function PrintView({ mice, towns, baseUrl, initialTown, onClose }) {
  const [mode, setMode] = useState('qr'); // qr | checklist
  const [layout, setLayout] = useState('label');
  const [town, setTown] = useState(initialTown || 'all');
  const [unfoundOnly, setUnfoundOnly] = useState(false);
  const [doubleSided, setDoubleSided] = useState(true);

  const selected = useMemo(
    () => mice.filter((m) => (town === 'all' || m.town === town) && (!unfoundOnly || m.status !== 'found')),
    [mice, town, unfoundOnly]
  );
  const cfg = LAYOUTS[layout];
  const pages = useMemo(() => chunk(selected, cfg.perPage), [selected, cfg.perPage]);
  // Every sheet keeps the full grid so front and back rows are the same height, and the back
  // mirrors each row so a long-edge flip puts every QR code behind its own front.
  const sheets = useMemo(() => pages.flatMap((page, pageIndex) => {
    const slots = Array.from({ length: cfg.perPage }, (_, i) => page[i] || null);
    const front = { key: `${pageIndex}-front`, label: `Sheet ${pageIndex + 1}${doubleSided ? ' · front' : ''}`, slots };
    if (!doubleSided) return [front];
    const back = chunk(slots, cfg.cols).flatMap((row) => [...row].reverse());
    return [front, { key: `${pageIndex}-back`, label: `Sheet ${pageIndex + 1} · back`, slots: back }];
  }), [pages, cfg.perPage, cfg.cols, doubleSided]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const byTown = useMemo(() => {
    const groups = new Map();
    selected.forEach((m) => { groups.set(m.town, [...(groups.get(m.town) || []), m]); });
    return [...groups.entries()];
  }, [selected]);

  return createPortal(
    <PrintRoot id="mice-print-root">
      <PrintGlobal />
      <PrintBar className="no-print">
        <Label>Print
          <Select value={mode} onChange={(e) => setMode(e.target.value)}>
            <option value="qr">QR codes</option>
            <option value="checklist">Placement checklist (staff only)</option>
          </Select>
        </Label>
        {mode === 'qr' && (
          <Label>Size
            <Select value={layout} onChange={(e) => setLayout(e.target.value)}>
              {Object.entries(LAYOUTS).map(([key, l]) => <option key={key} value={key}>{l.label}</option>)}
            </Select>
          </Label>
        )}
        <Label>Town
          <Select value={town} onChange={(e) => setTown(e.target.value)}>
            <option value="all">All towns</option>
            {towns.map((t) => <option key={t} value={t}>{t}</option>)}
          </Select>
        </Label>
        <Label style={{ flexDirection: 'row', alignItems: 'center', gap: '0.5rem', textTransform: 'none', letterSpacing: 0, fontWeight: 500 }}>
          <input type="checkbox" checked={unfoundOnly} onChange={(e) => setUnfoundOnly(e.target.checked)} />
          Only mice not yet found
        </Label>
        {mode === 'qr' && (
          <Label style={{ flexDirection: 'row', alignItems: 'center', gap: '0.5rem', textTransform: 'none', letterSpacing: 0, fontWeight: 500 }}>
            <input type="checkbox" checked={doubleSided} onChange={(e) => setDoubleSided(e.target.checked)} />
            Double-sided (same QR on both sides)
          </Label>
        )}
        <div className="spacer" />
        <div className="hint">
          {selected.length} {selected.length === 1 ? 'mouse' : 'mice'} · {mode === 'qr' ? `${pages.length} sheet${pages.length === 1 ? '' : 's'}${doubleSided ? ` (${sheets.length} printed sides)` : ''}. ` : ''}
          {mode === 'qr' && cfg.compact
            ? (cfg.hole
              ? 'Cut along the dashed lines. Punch the hole and tie a tag onto each mouse. '
              : 'Cut along the dashed lines and attach a label to each mouse. ')
            : ''}
          In the print dialog choose A4, scale 100% and turn headers and footers off.
          {mode === 'qr' && doubleSided && <> Set <strong>Two-sided</strong> to <strong>flip on long edge</strong>.</>}
        </div>
        <Btn type="button" $solid disabled={selected.length === 0} onClick={() => window.print()}>Print</Btn>
        <Btn type="button" onClick={onClose}>Close</Btn>
      </PrintBar>

      {selected.length === 0 && <p className="no-print" style={{ textAlign: 'center', padding: '3rem' }}>No mice match these choices.</p>}

      {mode === 'qr' && sheets.map((sheet) => (
        <React.Fragment key={sheet.key}>
          <SheetLabel className="no-print">{sheet.label}</SheetLabel>
          <Sheet className="sheet" $cols={cfg.cols} $cellW={cfg.cellW} $cellH={cfg.cellH}>
            {sheet.slots.map((m, slotIndex) => {
              if (!m) return <div key={`empty-${slotIndex}`} />;
              const qr = (
                <div className="qr">
                  <QRCodeSVG value={mouseUrl(m.code, baseUrl)} size={512} level={cfg.ecc || 'M'} marginSize={1} title={`QR code for ${m.town} mouse ${m.number}`} />
                </div>
              );
              if (cfg.compact) {
                return (
                  <Tag key={m.id} $compact $qr={cfg.qr} $brand={cfg.brand} $meta={cfg.meta}>
                    {cfg.hole && <div className="hole" aria-hidden="true" />}
                    {cfg.brand > 0 && <div className="brand">{cfg.brandText || 'Belvoir Castle Mice Trail'}</div>}
                    {qr}
                    <div className="meta">{`${m.town} · ${m.number}`}</div>
                  </Tag>
                );
              }
              return (
                <Tag key={m.id} $punch={cfg.punch} $gap={cfg.gap} $qr={cfg.qr} $logo={cfg.logo} $brand={cfg.brand} $cta={cfg.cta} $sub={cfg.sub} $meta={cfg.meta} $url={cfg.url}>
                  {cfg.punch && <div className="punch" aria-hidden="true" />}
                  <div className="brand">
                    <img src={belvoirLogo} alt="Belvoir Castle" />
                    <span>Mice Trail</span>
                  </div>
                  <div className="cta">Squeak! You found me!<MouseSymbol /></div>
                  <div className="sub">I&rsquo;m yours to keep! Scan me to name me and claim your reward.</div>
                  {qr}
                  <div className="meta">{m.town} · Mouse {m.number}</div>
                  <div className="url">{mouseUrl(m.code, baseUrl).replace(/^https?:\/\//, '')}</div>
                </Tag>
              );
            })}
          </Sheet>
        </React.Fragment>
      ))}

      {mode === 'checklist' && selected.length > 0 && (
        <Checklist className="sheet">
          <h2>Mice Trail placement checklist</h2>
          <p>Staff copy. Do not display. Tick each mouse once it is hidden in place.</p>
          {byTown.map(([name, list]) => (
            <div key={name}>
              <h3>{name} ({list.length})</h3>
              <table>
                <thead><tr><th className="tick">Placed</th><th>#</th><th>Code</th><th>Where it is hidden</th></tr></thead>
                <tbody>
                  {list.map((m) => (
                    <tr key={m.id}>
                      <td className="tick"><span className="box" /></td>
                      <td>{m.number}</td>
                      <td style={{ fontFamily: 'ui-monospace, Menlo, monospace' }}>{m.code}</td>
                      <td>{m.hidingSpot || '\u00a0'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </Checklist>
      )}
    </PrintRoot>,
    document.body
  );
}

// ---------------------------------------------------------------------------
// CSV
// ---------------------------------------------------------------------------

// Cells that start with = + - @ can run as formulas in spreadsheets, so neutralise them.
const csvCell = (value) => {
  let text = value == null ? '' : String(value);
  if (/^[=+\-@\t\r]/.test(text)) text = `'${text}`;
  return `"${text.replace(/"/g, '""')}"`;
};

function downloadFoundCsv(mice) {
  const header = ['Town', 'Mouse number', 'Code', 'Hidden at', 'Mouse name', 'Found by', 'Email', 'Found with', 'Found at (UTC)', 'Newsletter opt-in', 'Social photo consent', 'Social photo URL'];
  const rows = mice
    .filter((m) => m.status === 'found')
    .map((m) => [m.town, m.number, m.code, m.hidingSpot, m.mouseName, m.finderName, m.finderEmail, m.withWho, m.foundAt, m.marketingOptIn ? 'Yes' : 'No', m.socialConsent ? 'Yes' : 'No', m.socialPhotoUrl || '']);
  const csv = [header, ...rows].map((r) => r.map(csvCell).join(',')).join('\r\n');
  const url = URL.createObjectURL(new Blob([`\ufeff${csv}`], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = `mice-trail-found-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

const formatWhen = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

function SocialPhotos({ mice }) {
  const photos = mice.filter((m) => m.socialConsent && m.socialPhotoUrl);
  return (
    <JourneyBox>
      <h4>Photos for social media</h4>
      <p>
        These are pictures visitors took with their mouse and agreed we can share.
        Open a photo, save it, and post it from the Belvoir accounts.
      </p>
      {photos.length === 0 ? (
        <p style={{ marginBottom: 0 }}>No photos yet. They appear here after a family ticks the social media box on their keepsake.</p>
      ) : (
        <div className="photos">
          {photos.map((m) => (
            <a key={m.id} href={m.socialPhotoUrl} target="_blank" rel="noopener noreferrer">
              <img src={m.socialPhotoUrl} alt={`${m.mouseName || 'Mouse'} in ${m.town}`} />
              <span>{m.mouseName || 'Mouse'} · {m.town}</span>
              <small>{[m.finderName, formatWhen(m.socialPhotoAt)].filter(Boolean).join(' · ')}</small>
            </a>
          ))}
        </div>
      )}
    </JourneyBox>
  );
}

function JourneyPanel({ journey, busy, onChange, onSaveHints }) {
  const [hints, setHints] = useState(journey?.hints || []);
  useEffect(() => { setHints(journey?.hints || []); }, [journey]);
  if (!journey) return null;
  const current = journey.phases.find((p) => p.id === journey.phase);
  const next = journey.phases.find((p) => p.id === journey.next);
  return (
    <JourneyBox>
      <h4>Mouse journey on the map</h4>
      <p>
        Live phase: <span className="phase">{current?.label || journey.phase}</span>.
        The public map updates as soon as you change this. Next moves the story to the phase before Market Harborough, then into each town week.
      </p>
      <Toolbar>
        <Label>Phase
          <Select value={journey.phase} disabled={busy} onChange={(e) => onChange({ phase: e.target.value })}>
            {journey.phases.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
          </Select>
        </Label>
        <Btn type="button" disabled={busy || !journey.previous} onClick={() => onChange({ previous: true })}>Previous</Btn>
        <Btn type="button" $solid disabled={busy || !journey.next} onClick={() => onChange({ next: true })}>
          Next phase{next ? ` · ${next.label}` : ''}
        </Btn>
      </Toolbar>
      <p style={{ marginTop: '0.4rem' }}>Video clues appear as play buttons on the map. Paste an https video, YouTube or Bunny URL.</p>
      <div className="hints">
        {hints.map((hint, index) => (
          <div key={hint.id || index} className="hint-row">
            <Input value={hint.title} placeholder="Clue title" maxLength={80} onChange={(e) => setHints((rows) => rows.map((row, i) => (i === index ? { ...row, title: e.target.value } : row)))} />
            <Input value={hint.video} placeholder="https://… video URL" onChange={(e) => setHints((rows) => rows.map((row, i) => (i === index ? { ...row, video: e.target.value } : row)))} />
            <Input value={hint.caption} placeholder="Caption" maxLength={240} style={{ gridColumn: '1 / -1' }} onChange={(e) => setHints((rows) => rows.map((row, i) => (i === index ? { ...row, caption: e.target.value } : row)))} />
          </div>
        ))}
      </div>
      <Btn type="button" style={{ marginTop: '0.85rem' }} disabled={busy} onClick={() => onSaveHints(hints)}>Save video clues</Btn>
    </JourneyBox>
  );
}

const MiceTrailAdmin = () => {
  const [mice, setMice] = useState([]);
  const [journey, setJourney] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null); // { error, text }
  const [newTown, setNewTown] = useState('');
  const [newCount, setNewCount] = useState(10);
  const [baseUrl, setBaseUrl] = useState(PUBLIC_SITE_URL);
  const [printing, setPrinting] = useState(null); // null | { town }
  const [busy, setBusy] = useState(false);

  const fail = useCallback((err) => {
    setMessage({ error: true, text: err.message });
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, live] = await Promise.all([adminListMice(), adminGetJourney().catch(() => null)]);
      setMice(list);
      if (live) setJourney(live);
      setMessage(null);
    } catch (err) {
      fail(err);
    } finally {
      setLoading(false);
    }
  }, [fail]);

  useEffect(() => { load(); }, [load]);

  const towns = useMemo(() => [...new Set(mice.map((m) => m.town))], [mice]);
  const grouped = useMemo(() => towns.map((t) => ({ name: t, list: mice.filter((m) => m.town === t) })), [towns, mice]);
  const foundCount = mice.filter((m) => m.status === 'found').length;
  const photoCount = mice.filter((m) => m.socialConsent && m.socialPhotoUrl).length;

  const run = async (action, success) => {
    setBusy(true);
    try {
      await action();
      await load();
      if (success) setMessage({ error: false, text: success });
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  const create = (e) => {
    e.preventDefault();
    const town = newTown.trim();
    const count = parseInt(newCount, 10);
    if (!town) { setMessage({ error: true, text: 'Enter a town name.' }); return; }
    if (!(count >= 1 && count <= 50)) { setMessage({ error: true, text: 'Choose between 1 and 50 mice.' }); return; }
    run(async () => { await adminGenerateMice(town, count); setNewTown(''); }, `Created ${count} mice for ${town}. Print their QR codes below.`);
  };

  const saveSpot = (mouse, value) => {
    if (value === mouse.hidingSpot) return;
    run(() => adminUpdateMouse(mouse.id, { hidingSpot: value }));
  };

  const rename = (name) => {
    const to = window.prompt(`New name for "${name}"`, name);
    if (!to || !to.trim() || to.trim() === name) return;
    run(() => adminRenameTown(name, to.trim()), `Renamed ${name} to ${to.trim()}.`);
  };

  const reset = (m) => {
    const ok = window.confirm(
      `Reset ${m.town} mouse ${m.number}?\n\nThis erases the registration by ${m.finderName} (${m.mouseName}) and lets the mouse be found again. Only do this for test scans or mistakes.`
    );
    if (ok) run(() => adminResetMouse(m.id), `${m.town} mouse ${m.number} has been reset.`);
  };

  const remove = (m) => {
    if (window.confirm(`Delete ${m.town} mouse ${m.number}? Its QR code will stop working.`)) {
      run(() => adminDeleteMouse(m.id), 'Mouse deleted.');
    }
  };

  const changeJourney = (fields) => {
    run(async () => { setJourney(await adminSetJourney(fields)); }, 'The public mouse journey has been updated.');
  };

  return (
    <div>
      {message && <Banner $error={message.error} role="status">{message.text}</Banner>}

      <JourneyPanel
        journey={journey}
        busy={busy}
        onChange={changeJourney}
        onSaveHints={(hints) => changeJourney({ hints })}
      />

      <SocialPhotos mice={mice} />

      <Chips>
        <Chip><strong>{towns.length}</strong><span>Towns</span></Chip>
        <Chip><strong>{mice.length}</strong><span>Mice</span></Chip>
        <Chip><strong>{foundCount}</strong><span>Found</span></Chip>
        <Chip><strong>{mice.length - foundCount}</strong><span>Still hiding</span></Chip>
        <Chip><strong>{photoCount}</strong><span>Photos to share</span></Chip>
      </Chips>

      <Toolbar as="form" onSubmit={create}>
        <Label>Add a town
          <Input value={newTown} onChange={(e) => setNewTown(e.target.value)} placeholder="Town name" maxLength={120} />
        </Label>
        <Label>Mice
          <Input type="number" min="1" max="50" value={newCount} onChange={(e) => setNewCount(e.target.value)} style={{ width: 90 }} />
        </Label>
        <Btn type="submit" $solid disabled={busy}>Create mice &amp; QR codes</Btn>
      </Toolbar>

      <Toolbar>
        <Label style={{ flex: '1 1 260px' }}>QR codes point to
          <Input value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} />
        </Label>
        <Btn type="button" $solid disabled={mice.length === 0} onClick={() => setPrinting({ town: 'all' })}>Print QR codes</Btn>
        <Btn type="button" disabled={foundCount === 0} onClick={() => downloadFoundCsv(mice)}>Download finds (CSV)</Btn>
        <Btn type="button" disabled={loading} onClick={load}>{loading ? 'Refreshing…' : 'Refresh'}</Btn>
      </Toolbar>
      <p style={{ margin: '-0.5rem 0 1.25rem', fontSize: '0.82rem', color: '#6b7280' }}>
        Every mouse has its own QR code. Once a family registers a mouse it is ticked off here and cannot be registered again.
        Keep the printed QR sheets safe until the mice are hidden: anyone holding a code can register that mouse.
      </p>

      {mice.length === 0 && !loading && (
        <p style={{ color: '#6b7280' }}>No mice yet. Add the first town above, for example 10 mice for each of the five towns.</p>
      )}

      {grouped.map(({ name, list }) => {
        const found = list.filter((m) => m.status === 'found').length;
        return (
          <Town key={name}>
            <TownHead>
              <h4>{name}</h4>
              <span className="count">{found} of {list.length} found</span>
              <span className="spacer" />
              <Btn type="button" onClick={() => rename(name)} disabled={busy}>Rename</Btn>
              <Btn type="button" onClick={() => setPrinting({ town: name })}>Print this town</Btn>
            </TownHead>
            <TableWrap>
              <Table>
                <thead>
                  <tr><th>#</th><th>Status</th><th>Hidden at (staff note)</th><th>Found by</th><th>Mouse name</th><th>Found with</th><th>When</th><th /></tr>
                </thead>
                <tbody>
                  {list.map((m) => (
                    <tr key={m.id}>
                      <td><strong>{m.number}</strong><small className="code">{m.code}</small></td>
                      <td><Badge $found={m.status === 'found'}>{m.status === 'found' ? '✓ Found' : 'Hiding'}</Badge></td>
                      <td>
                        <SpotInput
                          defaultValue={m.hidingSpot}
                          key={`${m.id}-${m.hidingSpot}`}
                          placeholder="e.g. Window of the bakery"
                          maxLength={255}
                          onBlur={(e) => saveSpot(m, e.target.value.trim())}
                          aria-label={`Hiding place for ${name} mouse ${m.number}`}
                        />
                      </td>
                      <td>{m.finderName}{m.finderEmail && <small>{m.finderEmail}</small>}</td>
                      <td>{m.mouseName}</td>
                      <td>{m.withWho}</td>
                      <td>{formatWhen(m.foundAt)}</td>
                      <td className="actions">
                        <LinkBtn type="button" onClick={() => window.open(mouseUrl(m.code, baseUrl), '_blank', 'noopener')}>Open link</LinkBtn>
                        {m.status === 'found'
                          ? <LinkBtn type="button" $danger onClick={() => reset(m)}>Reset</LinkBtn>
                          : <LinkBtn type="button" $danger onClick={() => remove(m)}>Delete</LinkBtn>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </TableWrap>
          </Town>
        );
      })}

      {printing && (
        <PrintView mice={mice} towns={towns} baseUrl={baseUrl} initialTown={printing.town} onClose={() => setPrinting(null)} />
      )}
    </div>
  );
};

export default MiceTrailAdmin;
